import { z } from 'zod';
import { Id, Seq, Timestamp, singleLine } from './common';
import { NoContent, endpoint } from './endpoint';
import { lenient } from './lenient';
import { LIMITS } from './limits';
import { HistoryMode, MessagePreview } from './messages';
import { Person } from './users';

export const ConversationKind = z.enum(['direct', 'group']);
export type ConversationKind = z.infer<typeof ConversationKind>;

/** `left` = voluntary (read-only up to leftSeq); `removed` = forced (no access, not listed). */
export const MembershipState = z.enum(['active', 'left', 'removed']);
export type MembershipState = z.infer<typeof MembershipState>;

export const GroupRole = z.enum(['owner', 'admin', 'member']);
export type GroupRole = z.infer<typeof GroupRole>;

export const MyMembership = z.object({
  state: lenient(MembershipState, 'left'),
  role: lenient(GroupRole, 'member'),
  muted: z.boolean(),
  pinned: z.boolean(),
  /** Monotonic: never moves backwards (R24). */
  lastReadSeq: Seq,
  /** First message seq this member may read (history policy at join time). */
  historyStartSeq: Seq,
  joinedAt: Timestamp,
  leftAt: Timestamp.nullable(),
});
export type MyMembership = z.infer<typeof MyMembership>;

export const ConversationSummary = z.object({
  id: Id,
  kind: lenient(ConversationKind, 'group'),
  /** Group title; null for direct conversations (use `peer`). */
  title: z.string().nullable(),
  peer: Person.nullable(),
  memberCount: z.number().int().nonnegative(),
  /** Up to 4 active members for avatar stacks, viewer excluded. */
  memberPreview: z.array(Person),
  my: MyMembership,
  unreadCount: z.number().int().nonnegative(),
  /** An unread message mentions the viewer. */
  mentioned: z.boolean(),
  lastMessage: MessagePreview.nullable(),
  lastActivityAt: Timestamp,
  lastSeq: Seq,
  lastEventSeq: Seq,
  readOnly: z.boolean(),
  /** `other`: a reason added by a newer server. */
  readOnlyReason: lenient(z.enum(['left', 'peer_deactivated', 'other']), 'other').nullable(),
  historyMode: lenient(HistoryMode, 'after_join').nullable(),
  createdAt: Timestamp,
});
export type ConversationSummary = z.infer<typeof ConversationSummary>;

export const ConversationMember = z.object({
  person: Person,
  role: lenient(GroupRole, 'member'),
  joinedAt: Timestamp,
});
export type ConversationMember = z.infer<typeof ConversationMember>;

export const ConversationDetail = ConversationSummary.extend({
  members: z.array(ConversationMember),
  createdBy: Id.nullable(),
});
export type ConversationDetail = z.infer<typeof ConversationDetail>;

const conversationParam = z.object({ conversationId: Id });

export const conversationsEndpoints = {
  /** Active and left conversations (removed ones are omitted). Ordered by lastActivityAt desc. */
  list: endpoint({
    id: 'conversations.list',
    method: 'GET',
    path: '/api/v1/conversations',
    auth: 'session',
    response: z.object({ conversations: z.array(ConversationSummary) }),
  }),
  get: endpoint({
    id: 'conversations.get',
    method: 'GET',
    path: '/api/v1/conversations/:conversationId',
    auth: 'session',
    params: conversationParam,
    response: z.object({ conversation: ConversationDetail }),
  }),
  /** Get-or-create; there is exactly one direct conversation per pair (R17). */
  openDirect: endpoint({
    id: 'conversations.openDirect',
    method: 'POST',
    path: '/api/v1/conversations/direct',
    auth: 'session',
    body: z.object({ userId: Id }),
    response: z.object({ conversation: ConversationDetail, created: z.boolean() }),
  }),
  /**
   * Creates a group owned by the caller. Idempotent on clientRequestId.
   * Adding people to a direct chat creates a new group; the direct history is not shared.
   */
  createGroup: endpoint({
    id: 'conversations.createGroup',
    method: 'POST',
    path: '/api/v1/conversations/group',
    auth: 'session',
    body: z.object({
      clientRequestId: Id,
      title: singleLine({ max: LIMITS.maxGroupTitleChars }).optional(),
      memberIds: z.array(Id).min(1).max(LIMITS.maxGroupMembers),
      fromDirectId: Id.optional(),
    }),
    response: z.object({ conversation: ConversationDetail }),
    successStatus: 201,
  }),
  /** Group owner/admin only. Changing historyMode posts a system line and notifies members. */
  update: endpoint({
    id: 'conversations.update',
    method: 'PATCH',
    path: '/api/v1/conversations/:conversationId',
    auth: 'session',
    params: conversationParam,
    body: z.object({ title: singleLine({ min: 1, max: LIMITS.maxGroupTitleChars }).optional(), historyMode: HistoryMode.optional() }),
    response: z.object({ conversation: ConversationDetail }),
  }),
  addMembers: endpoint({
    id: 'conversations.addMembers',
    method: 'POST',
    path: '/api/v1/conversations/:conversationId/members',
    auth: 'session',
    params: conversationParam,
    body: z.object({ userIds: z.array(Id).min(1).max(LIMITS.maxGroupMembers) }),
    response: z.object({ conversation: ConversationDetail }),
  }),
  /** Forced removal by a group owner/admin: the removed member loses server access. */
  removeMember: endpoint({
    id: 'conversations.removeMember',
    method: 'DELETE',
    path: '/api/v1/conversations/:conversationId/members/:userId',
    auth: 'session',
    params: z.object({ conversationId: Id, userId: Id }),
    response: z.object({ conversation: ConversationDetail }),
  }),
  setMemberRole: endpoint({
    id: 'conversations.setMemberRole',
    method: 'PATCH',
    path: '/api/v1/conversations/:conversationId/members/:userId',
    auth: 'session',
    params: z.object({ conversationId: Id, userId: Id }),
    body: z.object({ role: z.enum(['admin', 'member']) }),
    response: z.object({ conversation: ConversationDetail }),
  }),
  /** Voluntary leave: history up to now stays readable; owners hand over to an admin first. */
  leave: endpoint({
    id: 'conversations.leave',
    method: 'POST',
    path: '/api/v1/conversations/:conversationId/leave',
    auth: 'session',
    params: conversationParam,
    response: z.object({ conversation: ConversationSummary }),
  }),
  updateMine: endpoint({
    id: 'conversations.updateMine',
    method: 'PATCH',
    path: '/api/v1/conversations/:conversationId/me',
    auth: 'session',
    params: conversationParam,
    body: z.object({ muted: z.boolean().optional(), pinned: z.boolean().optional() }),
    response: z.object({ conversation: ConversationSummary }),
  }),
  /** Advances the read pointer; lower values are ignored (no read regression). */
  markRead: endpoint({
    id: 'conversations.markRead',
    method: 'POST',
    path: '/api/v1/conversations/:conversationId/read',
    auth: 'session',
    params: conversationParam,
    body: z.object({ seq: Seq }),
    response: z.object({ lastReadSeq: Seq, unreadCount: z.number().int().nonnegative() }),
  }),
  /** "입력 중" (`typing.v1`): the other members see for a few seconds that the caller is typing here. Nothing is stored. */
  typing: endpoint({
    id: 'conversations.typing',
    method: 'POST',
    path: '/api/v1/conversations/:conversationId/typing',
    auth: 'session',
    params: conversationParam,
    response: NoContent,
  }),
};
